import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AgencyModal from './AgencyModal';
import { useAPI } from 'hooks';
import Locales from 'locales';
import AgencyFormContent from './AgencyFormContent';
import { addAgencySchema } from './agencyFormSchema';

const AddAgencyModal = ({ isOpen, onClose, categories, isReadOnly = false }) => {
  const { api } = useAPI();
  const queryClient = useQueryClient();

  const {
    reset,
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(addAgencySchema(categories)),
    defaultValues: {
      name: '',
      ifu: '',
      phone: '',
      address: '',
      ecobank_user_id: '',
      admin_first_name: '',
      admin_last_name: '',
      admin_email: '',
      category_ids: null,
    },
  });

  const { mutate: createAgency, isLoading: isCreating } = useMutation({
    meta: { agloCampaignWrite: true },
    mutationFn: (data) => api.create('agency/', data),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/stats/default/'] });
      toast.success(Locales.formatString(Locales.agency.toast.agency_added, { name: data.name }));
      reset();
      onClose();
    },
  });

  const onSubmit = (data) => {
    if (!isReadOnly) createAgency(data);
  };

  return (
    <AgencyModal
      isOpen={isOpen}
      onClose={onClose}
      title="Ajouter une société"
      description="Renseignez les informations de la société."
      formId="add-agency-form"
      submitLabel="Créer la société"
      savingLabel="Création en cours…"
      isSaving={isSubmitting || isCreating}
    >
      <form id="add-agency-form" onSubmit={handleSubmit(onSubmit)} className="min-w-0">
        <AgencyFormContent register={register} errors={errors} control={control} categories={categories} includeAdmin />
      </form>
    </AgencyModal>
  );
};

AddAgencyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})),
};

export default AddAgencyModal;
